/**
 * FORM ACTIONS COMPONENT
 * Reusable action buttons for all data entry forms
 * Integrates with sidebar layout
 * @version 1.0.0
 * @production-ready
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Download, BarChart3, Save, ArrowUpDown } from 'lucide-react';

export interface FormActionsProps {
  // View mode
  isCondensed: boolean;
  onToggleView: () => void;

  // Actions state
  isExporting: boolean;
  isSaving?: boolean;
  loadingSummary: boolean;

  // Data state
  hasData: boolean;

  // Callbacks
  onAddRow: () => void;
  onShowSummary: () => void;
  onSave: () => void;
  onExport: () => void;
  onSort?: () => void;

  // Customization
  addRowLabel?: string;
  saveLabel?: string;
  exportLabel?: string;
  summaryLabel?: string;
  sortLabel?: string;

  // Optional features
  hideExport?: boolean;
  hideSummary?: boolean;
  hideAddRow?: boolean;
  hideSort?: boolean; // Default true - Sort button hidden by default
  customActions?: React.ReactNode;
}

export const FormActions: React.FC<FormActionsProps> = ({
  isCondensed,
  onToggleView,
  isExporting,
  isSaving = false,
  loadingSummary,
  hasData,
  onAddRow,
  onShowSummary,
  onSave,
  onExport,
  onSort,
  addRowLabel = 'New Date',
  saveLabel = 'Save',
  exportLabel = 'Export',
  summaryLabel = 'Summary',
  sortLabel = 'Sort',
  hideExport = false,
  hideSummary = false,
  hideAddRow = false,
  hideSort = true, // Hide sort button by default - internal sorting still works
  customActions
}) => {
  const buttonBaseClass = "flex items-center gap-1 px-3 py-1 rounded text-xs text-white font-medium transition-all duration-200";

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {/* Save */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onSave}
        disabled={isSaving || isExporting || !hasData}
        className={`${buttonBaseClass} disabled:opacity-50 disabled:cursor-not-allowed`}
        style={{
          background: 'var(--color-secondary-orange)',
        }}
        title="Save data"
      >
        {isSaving ? (
          <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
        ) : (
          <Save size={12} />
        )}
        <span className="hidden sm:inline">{saveLabel}</span>
      </motion.button>

      {/* Summary */}
      {!hideSummary && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onShowSummary}
          disabled={loadingSummary}
          className={`${buttonBaseClass} disabled:opacity-50 disabled:cursor-not-allowed`}
          style={{
            background: 'var(--color-secondary-orange)',
          }}
          title="View summary of all data"
        >
          {loadingSummary ? (
            <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
          ) : (
            <BarChart3 size={12} />
          )}
          <span className="hidden sm:inline">{summaryLabel}</span>
        </motion.button>
      )}

      {/* Export to Excel */}
      {!hideExport && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onExport}
          disabled={isExporting || !hasData}
          className={`${buttonBaseClass} disabled:opacity-50 disabled:cursor-not-allowed`}
          style={{
            background: 'var(--color-secondary-orange)',
          }}
          title="Export data to Excel file"
        >
          {isExporting ? (
            <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
          ) : (
            <Download size={12} />
          )}
          <span className="hidden sm:inline">{exportLabel}</span>
        </motion.button>
      )}

      {/* Sort */}
      {!hideSort && onSort && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onSort}
          disabled={!hasData}
          className={`${buttonBaseClass} disabled:opacity-50 disabled:cursor-not-allowed`}
          style={{
            background: 'var(--color-secondary-orange)',
          }}
          title="Sort rows by date in ascending order"
        >
          <ArrowUpDown size={12} />
          <span className="hidden sm:inline">{sortLabel}</span>
        </motion.button>
      )}

      {/* Custom Actions */}
      {customActions}

      {/* Add Row */}
      {!hideAddRow && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onAddRow}
          className={buttonBaseClass}
          style={{
            background: 'var(--color-primary-green)',
          }}
          title="Add a new row"
        >
          <Plus size={12} />
          <span className="hidden sm:inline">{addRowLabel}</span>
        </motion.button>
      )}
    </div>
  );
};
